import React, { memo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { bool, func } from 'prop-types';
import useCartAnalytics from '../../../../hooks/analytics/useCartAnalytics';
import { logError } from '../../../global/utils/logger';
import GlobalModal from '../../../global/atoms/globalModal/GlobalModal';
import Button from '../../../global/atoms/button';
import AlertIcon from '../../../../resources/images/alert-triangle-yellow28.svg';
import './allItemsUnavailableModal.scss';
import { ENV_CONFIG } from '../../../../constants/envConfig';
import { allItemUnavailableModalDataLocator } from './dataLocator';
import { useAnalyticsContext } from '../../../../config/GoogleTagManagerEvents';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../../constants/analyticsConstants/Ecommerce';

const AllItemsUnavailableModal = ({ toggleModal, isCheckoutEnable }) => {
    const cartIntl = useIntl();
    const { sendEventsForAllItemsUnavailableCtaClick, sendEventsForAllItemsUnavailableClose } = useAnalyticsContext();
    const [{ sendCartErrorEvent }] = useCartAnalytics();

    useEffect(() => {
        try {
            sendCartErrorEvent({
                form_error_type: EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_ALL_ITEMS_UNAVAILABLE,
                form_error: cartIntl.formatMessage({ id: 'all-items-unavailable-modal:header' })
            });
        } catch (error) {
            logError(error, false, 'sendCartErrorEvent');
        }
    }, []);

    const handleClick = () => {
        try {
            toggleModal();
            sendEventsForAllItemsUnavailableCtaClick(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FORM_COMPLETED,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_ALL_ITEMS_UNAVAILABLE,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_INFORMATION,
                cartIntl.formatMessage({ id: 'all-items-unavailable-modal:ok' })
            );
        } catch (error) {
            logError(error, false, 'handleClick');
        }
    };

    const handleClose = () => {
        try {
            toggleModal();
            sendEventsForAllItemsUnavailableClose(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CTA_CLICKED,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CLOSE,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_BUTTON,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_MODAL,
                window?.location?.href
            );
        } catch (error) {
            logError(error, false, 'handleClose');
        }
    };

    const handleContact = () => {
        try {
            sendEventsForAllItemsUnavailableClose(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CTA_CLICKED,
                ENV_CONFIG.HELP_DESK_PHONE,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_HREF,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_MODAL,
                `tel:${ENV_CONFIG.HELP_DESK_PHONE}`
            );
        } catch (error) {
            logError(error, false, 'handleContact');
        }
    };

    const renderModalTitle = () => (
        <h6
            className="all-items-unavailable-modal__title"
            data-testid={allItemUnavailableModalDataLocator.all_items_unavailable_modal_title}>
            <AlertIcon aria-hidden="true" tabIndex="-1" />
            {cartIntl.formatMessage({ id: 'all-items-unavailable-modal:header' })}
        </h6>
    );

    const renderModalFooter = () => (
        <Button
            className="button button-primary button-block"
            onClick={handleClick}
            buttonAriaLabel={cartIntl.formatMessage({ id: 'all-items-unavailable-modal:ok' })}
            dataTestid={allItemUnavailableModalDataLocator.all_items_unavailable_modal_ok_btn}>
            {cartIntl.formatMessage({ id: 'all-items-unavailable-modal:ok' })}
        </Button>
    );

    const renderModalContent = () => (
        <>
            <div>
                {isCheckoutEnable
                    ? cartIntl.formatMessage({ id: 'alteast-one-item-available-modal:content' })
                    : cartIntl.formatMessage({ id: 'all-items-unavailable-modal:content' })}
            </div>
            <div>
                {cartIntl.formatMessage({ id: 'all-items-unavailable-modal:call-us-at' })}
                <a href={`tel:${ENV_CONFIG.HELP_DESK_PHONE}`} className="helpline-number" onClick={handleContact}>
                    {ENV_CONFIG.HELP_DESK_PHONE}
                </a>
                .
            </div>
        </>
    );

    return (
        <GlobalModal
            isOpen
            className="all-items-unavailable-modal"
            customTitle={renderModalTitle()}
            footer={renderModalFooter()}
            content={renderModalContent()}
            onRequestClose={handleClose}
            handleClose={handleClose}
        />
    );
};

AllItemsUnavailableModal.defaultProps = {
    toggleModal: func,
    isCheckoutEnable: bool
};

AllItemsUnavailableModal.propTypes = {
    toggleModal: () => {},
    isCheckoutEnable: false
};

export default memo(AllItemsUnavailableModal);
